import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import type { PropsWithChildren, ReactElement } from 'react';

/**
 * How much control a surface offers.
 *
 * `simple` is the automatic view: sensible defaults, a short vocabulary, and the
 * plain-language answer first. `advanced` opens every control the surface has —
 * the columnar reading, the machine identifiers, the display and retention
 * controls. It is a READER preference, not a permission: nothing is unlocked by
 * switching, and nothing a `simple` reader can see is hidden from them.
 */
export type ControlMode = 'simple' | 'advanced';

interface ControlModeContextValue {
  mode: ControlMode;
  /** Sugar for the overwhelmingly common `mode === 'advanced'` read. */
  isAdvanced: boolean;
  setMode: (mode: ControlMode) => void;
  toggleMode: () => void;
}

const STORAGE_KEY = 'syncanix:control-mode';
const ControlModeContext = createContext<ControlModeContextValue | undefined>(undefined);

function isControlMode(value: unknown): value is ControlMode {
  return value === 'simple' || value === 'advanced';
}

/**
 * The stored preference, or `simple`.
 *
 * Unlike the theme there is no system signal to fall back to, and no
 * `prefers-*` query that means "show me more controls" — so an unconfigured
 * reader gets the automatic view, which is the point of the mode existing.
 *
 * Both accessors are guarded: `localStorage` THROWS rather than returning null
 * in a private window and under a block-site-data setting, and a preference is
 * never worth taking the app down for.
 */
function readInitialMode(): ControlMode {
  if (typeof window === 'undefined') return 'simple';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isControlMode(stored) ? stored : 'simple';
  } catch {
    return 'simple';
  }
}

function persist(mode: ControlMode): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // A lost preference is a smaller failure than a crashed render.
  }
}

/**
 * App-wide Simple ⇄ Advanced preference, persisted per browser.
 *
 * It lives in a provider rather than in the one page that reads it today
 * because the switch is in the TOP BAR's user menu — chrome that outlives any
 * page — and because the preference is explicitly cross-page: a reader who asks
 * for advanced controls has asked every surface that supports them, not just
 * the one they happened to be on. `/system-logs` is the first consumer.
 */
export interface ControlModeProviderProps extends PropsWithChildren {
  /**
   * Seed the mode explicitly instead of reading the stored preference — the
   * same escape hatch `I18nProvider` and `WorkspaceProvider` offer. Tests use
   * it so a mode never leaks between cases through the shared storage key,
   * which is exactly how the design prototype kept opening in the wrong mode.
   */
  initialMode?: ControlMode;
}

export function ControlModeProvider({
  children,
  initialMode,
}: ControlModeProviderProps): ReactElement {
  const [mode, setModeState] = useState<ControlMode>(() => initialMode ?? readInitialMode());

  const setMode = useCallback((next: ControlMode) => {
    setModeState(next);
    persist(next);
  }, []);

  const toggleMode = useCallback(() => {
    setModeState((prev) => {
      const next: ControlMode = prev === 'simple' ? 'advanced' : 'simple';
      persist(next);
      return next;
    });
  }, []);

  const value = useMemo<ControlModeContextValue>(
    () => ({ mode, isAdvanced: mode === 'advanced', setMode, toggleMode }),
    [mode, setMode, toggleMode],
  );

  return <ControlModeContext.Provider value={value}>{children}</ControlModeContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- co-located hook by design
export function useControlMode(): ControlModeContextValue {
  const ctx = useContext(ControlModeContext);
  if (!ctx) {
    throw new Error('useControlMode must be used within a ControlModeProvider');
  }
  return ctx;
}
